/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC } from 'react';
import { fade, InputAdornment, makeStyles, TextField, TextFieldProps, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { RevertButton } from './components/RevertButton';

export type OverrideInputProps = TextFieldProps & {
  isOverridden?: boolean;
  value?: any;
  sourceValue?: any;
  renderInput?: FC<any>;
  InputProps?: Record<string, unknown>;
  onChange?: (value: any) => void;
  onRevert?: (event?: React.MouseEvent) => void;
  revertButtonTabIndex?: number | undefined;
  disableTooltip?: boolean;
  showEndAdornment?: boolean;
  hideOverrideStyles?: boolean;
  hideRevertButton?: boolean;
  isFlowText?: boolean;
};

export const useOverrideStyles = makeStyles((theme: Theme) => ({
  override: {
    '&.override': {
      '& label': {
        color: theme.palette.warning.dark,
      },
      '& .MuiInput-underline:before': {
        borderBottomColor: theme.palette.warning.dark,
      },
      '& .MuiInput-underline:after': {
        borderBottomColor: theme.palette.warning.dark,
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: theme.palette.warning.dark,
        },
        '&:hover fieldset': {
          borderColor: theme.palette.warning.dark,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.palette.warning.dark,
        },
      },
      '& .MuiFilledInput-root, & .MuiFilledInput-root:focus, & .MuiFilledInput-root:hover': {
        backgroundColor: fade(theme.palette.warning.light, 0.2),
      },
      '& .MuiFilledInput-underline:before': {
        borderBottomColor: theme.palette.warning.dark,
      },
      '& .MuiFilledInput-underline:after': {
        borderBottomColor: theme.palette.warning.dark,
      },
      '& .MuiFormHelperText-root': {
        color: theme.palette.warning.dark,
      },
    },
  },
  error: {
    '&.error': {
      '& label': {
        color: theme.palette.error.dark,
      },
      '& .MuiInput-underline:before': {
        borderBottomColor: theme.palette.error.dark,
      },
      '& .MuiInput-underline:after': {
        borderBottomColor: theme.palette.error.dark,
      },
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: theme.palette.error.dark,
        },
        '&:hover fieldset': {
          borderColor: theme.palette.error.dark,
        },
        '&.Mui-focused fieldset': {
          borderColor: theme.palette.error.dark,
        },
      },
      '& .MuiFilledInput-root, & .MuiFilledInput-root:focus, & .MuiFilledInput-root:hover': {
        backgroundColor: fade(theme.palette.error.light, 0.2),
      },
      '& .MuiFilledInput-underline:before': {
        borderBottomColor: theme.palette.error.dark,
      },
      '& .MuiFilledInput-underline:after': {
        borderBottomColor: theme.palette.error.dark,
      },
      '& .MuiFormHelperText-root': {
        color: theme.palette.error.dark,
      },
    },
  },
  overrideMultiline: {
    '&& .MuiInputBase-inputMultiline': {
      whiteSpace: 'pre-wrap',
    },
  },
}));

export function OverrideInput({
  isOverridden = false,
  value,
  sourceValue,
  renderInput = (props) => <TextField {...props} />,
  InputLabelProps,
  InputProps,
  onChange = () => undefined,
  onRevert = () => undefined,
  disableTooltip = false,
  revertButtonTabIndex,
  showEndAdornment = false,
  hideOverrideStyles = false,
  hideRevertButton = false,
  isFlowText = false,
  ...props
}: OverrideInputProps): JSX.Element {
  const classes = useOverrideStyles();

  // Warning: `value` prop on `textarea` should not be null.
  // Consider using an empty string to clear the component or `undefined` for uncontrolled components
  // NOTE: Can't use empty string here as the value for an overrideField (at the moment) is either string | number
  // 0 is not a valid "non value" so we need to return undefined
  const currentValue = (value: string | number | undefined): string | number | undefined => {
    return value ?? undefined;
  };

  return (
    <>
      {renderInput({
        'data-testid': 'override-input',
        ...props,
        fullWidth: true,
        value: isOverridden ? currentValue(value) : currentValue(sourceValue),
        onChange,
        'data-isoverridden': isOverridden,
        className: clsx(
          classes.override,
          !hideOverrideStyles && isOverridden && !props.error && 'override',
          props.error && 'error',
          props.className,
          isFlowText && classes.overrideMultiline,
        ),
        InputLabelProps: {
          ...InputLabelProps,
        },
        InputProps: {
          ...InputProps,
          endAdornment: (
            <InputAdornment position="end">
              {(!isOverridden || showEndAdornment) && InputProps?.endAdornment}
              {isOverridden &&
                (hideRevertButton ? (
                  <></>
                ) : (
                  <RevertButton onRevert={onRevert} disableTooltip={disableTooltip} tabIndex={revertButtonTabIndex} />
                ))}
            </InputAdornment>
          ),
        },
      })}
    </>
  );
}

export default OverrideInput;
